import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { catchError, map, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../common/services/notification/notification.service';
import { ProfileService } from './profile.service';
import {
  ProfileAcceptMapsPlugin,
  ProfileChangePassword,
  ProfileDelete,
  ProfileInitialState,
  ProfileLoad,
  ProfileSetFromAuthMe,
  ProfileUpdate,
  ProfileUpdatePassword,
} from './profile.actions';
import { AuthFacade, AuthPatchUserData, AuthReloadUserData } from '../../auth';
import { HubContextRecalculateTimezone } from '../hub/context/hub-context.actions';
import { ProfileModel, ProfileResponseModel } from './models';
import { Error, CustomHttpErrorResponse } from '../../../../common/constants/error';
import { Role } from '../../../../common/constants/role';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface ProfileStateModel extends ProfileModel {
  loaded: boolean;
}

const initialState: ProfileStateModel = {
  ...new ProfileModel(),
  loaded: false,
};

function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (Object.values(Role) as string[]).includes(value);
}

@State<ProfileStateModel>({
  name: 'profile',
  defaults: initialState,
})
@Injectable()
export class ProfileState {
  constructor(
    private profileService: ProfileService,
    private notificationService: NotificationService,
    private authFacade: AuthFacade,
  ) {}

  @Selector()
  static getState(state: ProfileStateModel): ProfileStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: ProfileStateModel): boolean {
    return state.loaded;
  }

  @Action(ProfileLoad)
  getUserDetails(ctx: StateContext<ProfileStateModel>): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.profileService.getProfile().pipe(
      take(1),
      map((profileData: ProfileModel) => {
        ctx.patchState({ ...profileData, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ProfileUpdate)
  updateUser(
    ctx: StateContext<ProfileStateModel>,
    { payload }: ProfileUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.profileService.updateProfile(payload).pipe(
      take(1),
      map((profileData: ProfileResponseModel) => {
        this.notificationService.success('api.profile.success.update');
        ctx.patchState({ ...profileData.profile, loaded: true });
        ctx.dispatch([
          new AuthPatchUserData({
            twentyFourHourTime: profileData.profile.twentyFourHourTime,
            defaultLanguage: profileData.profile.defaultLanguage,
            country: profileData.profile.country,
            timezone: profileData.profile.timezone,
            newsSubscribed: profileData.profile.newsSubscribed,
            renderTimezone: profileData.profile.renderTimezone,
            mapsPluginAccepted: profileData.profile.mapsPluginAccepted,
          }),
          new HubContextRecalculateTimezone(),
        ]);
        ctx.dispatch(new AuthReloadUserData());
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ProfileChangePassword)
  changePassword(
    ctx: StateContext<ProfileStateModel>,
    { payload: { body, dialogRef } }: ProfileChangePassword,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.profileService.changePassword(body).pipe(
      take(1),
      map(() => {
        this.notificationService.success('api.profile.success.changePassword');
        dialogRef.close();
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ProfileDelete)
  deleteUser(
    ctx: StateContext<ProfileStateModel>,
    { payload }: ProfileDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.profileService.deleteProfile().pipe(
      take(1),
      map(() => {
        payload.dialog.close();
        this.notificationService.success('api.user.success.delete');
        this.authFacade.logout();
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ProfileUpdatePassword)
  updatePassword(
    ctx: StateContext<ProfileStateModel>,
    { payload: { body } }: ProfileUpdatePassword,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.profileService.changePassword(body).pipe(
      take(1),
      map(() => {
        this.notificationService.success('api.profile.success.changePassword');
        this.authFacade.logout();
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ProfileAcceptMapsPlugin)
  acceptMapsPlugin(ctx: StateContext<ProfileStateModel>): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.profileService.acceptMapsPlugin().pipe(
      take(1),
      catchError(() => of(void 0)),
      map(() => {
        ctx.patchState({ mapsPluginAccepted: true });
        ctx.dispatch(new AuthPatchUserData({ mapsPluginAccepted: true }));
      }),
    );
  }

  @Action(ProfileInitialState)
  setInitialState(ctx: StateContext<ProfileStateModel>): void {
    ctx.setState(initialState);
  }

  @Action(ProfileSetFromAuthMe)
  setFromAuthMe(ctx: StateContext<ProfileStateModel>, { payload }: ProfileSetFromAuthMe): void {
    const me = payload;

    const rolesList: Role[] = Array.isArray(me.roles) ? me.roles.filter(isRole) : [];

    ctx.patchState({
      id: me.id,
      email: me.email,
      firstName: me.user?.firstName ?? '',
      lastName: me.user?.lastName ?? '',
      defaultLanguage: me.defaultLanguage,
      country: me.country,
      timezone: me.user?.timezone ?? '',
      twentyFourHourTime: me.twentyFourHourTime,
      renderTimezone: me.user?.renderTimezone,
      newsSubscribed: me.newsSubscribed,

      roles: rolesList,
      approvedRoles: rolesList,
    });
  }
}
